import { useEffect, useRef, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useAtomValue, useSetAtom } from 'jotai';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Inbox, Users, Briefcase, Settings, LogOut, Wifi } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { SettingsDialog } from './SettingsDialog';
import { tipsAtom, fetchTipsActionAtom } from '../stores/tipsStore';
import {
  assignmentsAtom,
  fetchAssignmentsActionAtom,
} from '../stores/assignmentsStore';
import { currentUserAtom, logoutActionAtom } from '../stores/authStore';
import { toast } from 'sonner';

export function Navigation() {
  const location = useLocation();
  const tips = useAtomValue(tipsAtom);
  const assignments = useAtomValue(assignmentsAtom);
  const user = useAtomValue(currentUserAtom);
  const fetchTips = useSetAtom(fetchTipsActionAtom);
  const fetchAssignments = useSetAtom(fetchAssignmentsActionAtom);
  const logout = useSetAtom(logoutActionAtom);
  const hasFetchedRef = useRef(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Fetch data once on navigation mount with error handling
  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    const loadData = async () => {
      try {
        await fetchTips();
      } catch (error) {
        console.debug('Error fetching tips in navigation:', error);
      }

      try {
        await fetchAssignments();
      } catch (error) {
        console.debug('Error fetching assignments in navigation:', error);
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Calculate counts from current data
  const pendingTipsCount = tips.filter(tip => tip.status === 'pending').length;
  const totalTipsCount = tips.length;
  const activeAssignmentsCount = assignments.filter(
    assignment => !['published', 'cancelled'].includes(assignment.status)
  ).length;

  const handleLogout = async () => {
    try {
      await logout();
      toast.success('Signed out successfully');
    } catch (error) {
      toast.error('Error signing out');
    }
  };

  const getUserInitials = () => {
    if (user?.first_name && user?.last_name) {
      return `${user.first_name[0]}${user.last_name[0]}`.toUpperCase();
    }
    if (user?.email && user.email.length > 0) {
      return user.email[0]!.toUpperCase();
    }
    return 'U';
  };

  const getUserDisplayName = () => {
    if (user?.first_name && user?.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
    if (user?.first_name) {
      return user.first_name;
    }
    return user?.email || 'User';
  };

  const getUserEmail = () => {
    return user?.email || '';
  };

  const navigationItems = [
    {
      id: 'inbox',
      path: '/inbox',
      label: 'Tip Inbox',
      icon: Inbox,
      badge: pendingTipsCount > 0 ? pendingTipsCount : undefined,
    },
    {
      id: 'sources',
      path: '/sources',
      label: 'Sources',
      icon: Users,
    },
    {
      id: 'assignments',
      path: '/assignments',
      label: 'Assignments',
      icon: Briefcase,
      badge: activeAssignmentsCount > 0 ? activeAssignmentsCount : undefined,
    },
  ];

  if (location.pathname.startsWith('/conversation')) return null;

  return (
    <div className='w-64 bg-card border-r border-border h-screen flex flex-col'>
      <div className='p-6 border-b border-border text-left'>
        <div className='flex items-center gap-2 mb-2'>
          <h1 className='font-semibold text-xl'>Verity Loop</h1>
        </div>
        <p className='text-muted-foreground text-sm'>AI Journalism Platform</p>
        <div className='text-xs text-muted-foreground mt-1 space-y-1'>
          {totalTipsCount > 0 && (
            <p>
              {totalTipsCount} tips total
              {pendingTipsCount > 0 && ` • ${pendingTipsCount} pending`}
            </p>
          )}
          {activeAssignmentsCount > 0 && (
            <p>{activeAssignmentsCount} active assignments</p>
          )}
        </div>
      </div>

      <nav className='flex-1 p-4'>
        <div className='space-y-2'>
          {navigationItems.map(item => {
            const Icon = item.icon;
            const isActive =
              location.pathname === item.path ||
              location.pathname.startsWith(item.path + '/');

            return (
              <Button
                key={item.id}
                variant={isActive ? 'default' : 'ghost'}
                className='w-full justify-start gap-3 h-12'
                asChild
              >
                <Link to={item.path}>
                  <Icon className='h-5 w-5' />
                  <span className='flex-1 text-left'>{item.label}</span>
                  {item.badge && (
                    <Badge variant='secondary' className='ml-auto'>
                      {item.badge}
                    </Badge>
                  )}
                </Link>
              </Button>
            );
          })}
        </div>
      </nav>

      <div className='p-4 border-t border-border'>
        <div className='flex items-center gap-3 mb-4'>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant='ghost'
                className='w-full h-auto p-2 justify-start gap-3'
              >
                <Avatar className='h-8 w-8'>
                  <AvatarFallback className='text-xs'>
                    {getUserInitials()}
                  </AvatarFallback>
                </Avatar>
                <div className='flex-1 text-left min-w-0'>
                  <p className='text-sm font-medium truncate'>
                    {getUserDisplayName()}
                  </p>
                  <p className='text-xs text-muted-foreground truncate'>
                    {getUserEmail()}
                  </p>
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end' className='w-56'>
              <DropdownMenuLabel>My Account</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setSettingsOpen(true)}>
                <Settings className='mr-2 h-4 w-4' />
                <span>Settings</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout}>
                <LogOut className='mr-2 h-4 w-4' />
                <span>Sign out</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <SettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />
        </div>

        {/* Data Source Status */}
        <div className='text-xs'>
          <div className='flex items-center gap-2 mb-1'>
            <Wifi className='h-3 w-3 text-green-500' />
            <span className='text-green-600'>Live API</span>
          </div>

          <p className='text-xs text-muted-foreground'>
            Connected to live server
          </p>
        </div>
      </div>
    </div>
  );
}
